import { getApiBaseUrl } from "src/config/endpoints";
import { normalizeApiLang, type ApiLang } from "src/utils/apiLang";
import { buildAuthHeaders, getAuthToken, handleJsonResponse } from "./httpUtils";

const API_BASE_URL = getApiBaseUrl();

const buildPrimaryDateParams = (primary_date: string, lang: ApiLang) => {
  const searchParams = new URLSearchParams({
    primary_date,
    lang: normalizeApiLang(lang),
    api_key: getAuthToken("api_key"),
  });
  return searchParams.toString();
};

const buildKpiParams = (primary_date: string, secondary_date: string, lang: ApiLang) => {
  const searchParams = new URLSearchParams({
    primary_date,
    secondary_date,
    lang: normalizeApiLang(lang),
    api_key: getAuthToken("api_key"),
  });
  return searchParams.toString();
};

const toNumber = (value: unknown) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : undefined;
};

const toStringValue = (value: unknown) =>
  value === null || value === undefined ? "" : String(value);

const pick = <T = unknown>(source: Record<string, unknown>, keys: string[]): T | undefined => {
  for (const key of keys) {
    if (source[key] !== undefined && source[key] !== null) {
      return source[key] as T;
    }
  }
  return undefined;
};

// ── Types (match backend CLTV dashboard schemas) ─────────────────────────────

export type CLTVKPICard = {
  id: string;
  title: string;
  value: string | number;
  formattedValue?: string;
  /** Optional in practice — newer KPI ids ship without one. */
  subtitle?: string;
  percentageChange: number;
  /** Localized change string from the API, e.g. "1.2%" / "١.٢%". */
  percentageChangeFormattedValue?: string;
  /** Optional in practice — most KPI ids ship without one. */
  badge?: string;
};

export type SegmentDistributionItem = {
  /** Localized display label (`lang=ar` returns the translated segment name, not a code). */
  name: string;
  /**
   * Canonical English segment name, resolved by `fetchCLTVKpis` — the key the CLTV segment
   * palette and the `cltv.segmentNames.*` copy are addressed by. `name` cannot serve as that key
   * under `lang=ar`: every row would miss the palette and the chart would lose the per-segment
   * colors it shows in English.
   *
   * TODO(backend): `segmentDistribution[]` should expose the stable English segment id (the same
   * one the `segment` filter param accepts) so the companion `lang=en` request below can be
   * dropped.
   */
  nameKey?: string;
  count: number;
  countFormattedValue?: string;
  percent: number;
};

export type RiskTier = {
  /** Localized display label (`lang=ar` returns the translated band name, not a code). */
  tier: string;
  count: number;
  countFormattedValue?: string;
  percentage: number;
  percentageFormattedValue?: string;
};

export type PredictiveInsightItem = {
  id: string;
  title: string;
  value?: string | number;
  formattedValue?: string;
  description?: string;
  badge?: string;
  actionLabel?: string;
  count?: number;
  countLabel?: string;
  tiers?: RiskTier[];
};

export type CLTVKPIResponse = {
  kpiCards: CLTVKPICard[];
  segmentDistribution: SegmentDistributionItem[];
  predictiveInsights: PredictiveInsightItem[];
};

export type SpendingPatternsResponse = {
  data: {
    bucket?: string;
    month?: string;
    actual: number;
    predicted: number;
    actualFormattedValue?: string;
    predictedFormattedValue?: string;
  }[];
};

export type SegmentEvolutionResponse = {
  data: {
    date: string;
    loyal?: number;
    loyal_customers?: number;
    highSpender?: number;
    high_spenders?: number;
    likelyToChurn?: number;
    likely_to_churn?: number;
    oneTimer?: number;
    one_timers?: number;
    idle?: number;
    idle_customers?: number;
    cartAbandoner?: number;
    cartAbandoners?: number;
    cart_abandoners?: number;
    indecisive?: number;
    indecisive_buyers?: number;
    unsubscribed?: number;
  }[];
};

export type CustomerAnalyticsRow = {
  customerId: string;
  email: string;
  customerName?: string;
  segment?: string | string[];
  customerAgeMonths?: number;
  totalSpendSince2023?: number;
  totalSpendFormattedValue?: string;
  totalOrdersSince2023?: number;
  predictedSpend3m?: number;
  predictedSpend3mFormattedValue?: string;
  predictedOrders3m?: number;
  predictedLtv90d?: number;
  predictedLtv90dFormattedValue?: string;
  segmentLoyal?: number;
  segmentHighSpenders?: number;
  segmentIdle?: number;
  segmentOneTimers?: number;
  segmentLikelyToChurn?: number;
  segmentCartAbandoners?: number;
  segmentIndecisive?: number;
  segmentUnsubscribed?: number;
  likelyToChurn?: boolean;
  orders_0_30d?: number;
  orders_30_60d?: number;
  orders_60_90d?: number;
  spend_0_30d?: number;
  spend_30_60d?: number;
  spend_60_90d?: number;
  predOrders_0_30d?: number;
  predOrders_30_60d?: number;
  predSpend_0_30d?: number;
  predSpend_30_60d?: number;
};

export type CustomerAnalyticsResponse = {
  data: CustomerAnalyticsRow[];
  pagination: {
    page: number;
    pageSize: number;
    totalRecords: number;
    totalPages: number;
  };
};

export interface FetchCustomerAnalyticsParams {
  primary_date: string;
  lang: ApiLang;
  page?: number;
  page_size?: number;
  search?: string;
  segment?: string[];
  sort_by?: string;
  sort_order?: "asc" | "desc";
}

export type CustomerAnalyticsSegmentsResponse = {
  /**
   * Translated *display labels*, not filter ids — the backend changed this endpoint from raw ids
   * (`["idle", "one_timer", …]`) to localized strings, so nothing here can be sent back as the
   * `segment` filter param. That param still only accepts the fixed English ids:
   * `idle | one_timer | high_spender | unsubscribed | loyal | cart_abandoner | indecisive |
   * likely_to_churn` — which is why the segment dropdown is built from `SEGMENT_PILL_FILTERS`
   * (ids + our own copy) rather than from this response.
   *
   * Nothing reads these labels today. Ask the backend for the `{id, label}` shape they offered
   * before wiring them to any filter.
   */
  segments: string[];
};

const normalizeCustomerAnalyticsRow = (raw: unknown): CustomerAnalyticsRow => {
  const row = (raw ?? {}) as Record<string, unknown>;
  const customerName = pick<string>(row, ["customerName", "customer_name", "name"]);
  const customerId = pick(row, ["customerId", "customer_id", "id"]) ?? customerName ?? pick(row, ["email"]);
  const profileYears = toNumber(pick(row, ["profileYears", "profile_years"]));

  return {
    customerId: toStringValue(customerId),
    email: toStringValue(pick(row, ["email", "customerEmail", "customer_email"])),
    customerName,
    segment: pick<string | string[]>(row, ["segment", "segments", "customerSegment", "customer_segment"]),
    customerAgeMonths:
      toNumber(pick(row, ["customerAgeMonths", "customer_age_months"])) ??
      (profileYears !== undefined ? Math.max(1, Math.round(profileYears * 12)) : undefined),
    totalSpendSince2023: toNumber(pick(row, ["totalSpendSince2023", "total_spend_since_2023", "totalSpend", "total_spend"])),
    totalSpendFormattedValue: pick<string>(row, ["totalSpendFormattedValue", "total_spend_formatted_value"]),
    totalOrdersSince2023: toNumber(pick(row, ["totalOrdersSince2023", "total_orders_since_2023", "totalOrders", "total_orders"])),
    predictedSpend3m: toNumber(pick(row, ["predictedSpend3m", "predicted_spend_3m", "predictedSpend", "predicted_spend"])),
    predictedSpend3mFormattedValue: pick<string>(row, ["predictedSpend3mFormattedValue", "predicted_spend_3m_formatted_value"]),
    predictedOrders3m: toNumber(pick(row, ["predictedOrders3m", "predicted_orders_3m", "predictedOrders", "predicted_orders"])),
    predictedLtv90d: toNumber(pick(row, ["predictedLtv90d", "predicted_ltv_90d", "predictedCltv", "predicted_cltv"])),
    predictedLtv90dFormattedValue: pick<string>(row, ["predictedLtv90dFormattedValue", "predicted_ltv_90d_formatted_value"]),
    segmentLoyal: toNumber(pick(row, ["segmentLoyal", "segment_loyal", "loyal"])),
    segmentHighSpenders: toNumber(pick(row, ["segmentHighSpenders", "segment_high_spenders", "segment_high_spender", "high_spender"])),
    segmentIdle: toNumber(pick(row, ["segmentIdle", "segment_idle", "idle"])),
    segmentOneTimers: toNumber(pick(row, ["segmentOneTimers", "segment_one_timers", "segment_one_timer", "one_timer"])),
    segmentLikelyToChurn: toNumber(pick(row, ["segmentLikelyToChurn", "segment_likely_to_churn", "likely_to_churn"])),
    segmentCartAbandoners: toNumber(pick(row, ["segmentCartAbandoners", "segment_cart_abandoners", "segment_cart_abandoner", "cart_abandoner"])),
    segmentIndecisive: toNumber(pick(row, ["segmentIndecisive", "segment_indecisive", "indecisive"])),
    segmentUnsubscribed: toNumber(pick(row, ["segmentUnsubscribed", "segment_unsubscribed", "unsubscribed"])),
    likelyToChurn: Boolean(pick(row, ["likelyToChurn", "likely_to_churn"])),
    orders_0_30d: toNumber(pick(row, ["orders_0_30d"])),
    orders_30_60d: toNumber(pick(row, ["orders_30_60d"])),
    orders_60_90d: toNumber(pick(row, ["orders_60_90d"])),
    spend_0_30d: toNumber(pick(row, ["spend_0_30d"])),
    spend_30_60d: toNumber(pick(row, ["spend_30_60d"])),
    spend_60_90d: toNumber(pick(row, ["spend_60_90d"])),
    predOrders_0_30d: toNumber(pick(row, ["predOrders_0_30d", "pred_orders_0_30d"])),
    predOrders_30_60d: toNumber(pick(row, ["predOrders_30_60d", "pred_orders_30_60d"])),
    predSpend_0_30d: toNumber(pick(row, ["predSpend_0_30d", "pred_spend_0_30d"])),
    predSpend_30_60d: toNumber(pick(row, ["predSpend_30_60d", "pred_spend_30_60d"])),
  };
};

const normalizeCustomerAnalyticsResponse = (raw: unknown): CustomerAnalyticsResponse => {
  const payload = (raw ?? {}) as Record<string, unknown>;
  const pagination = (payload.pagination ?? {}) as Record<string, unknown>;
  const data = Array.isArray(payload.data) ? payload.data : [];
  const pageSize = toNumber(pick(pagination, ["pageSize", "page_size"])) ?? 10;
  const totalRecords = toNumber(pick(pagination, ["totalRecords", "total_records", "total"])) ?? data.length;

  return {
    data: data.map(normalizeCustomerAnalyticsRow),
    pagination: {
      page: toNumber(pick(pagination, ["page"])) ?? 1,
      pageSize,
      totalRecords,
      totalPages:
        toNumber(pick(pagination, ["totalPages", "total_pages"])) ??
        Math.max(1, Math.ceil(totalRecords / pageSize)),
    },
  };
};


const fetchKpiBundle = async (
  primary_date: string,
  secondary_date: string,
  lang: ApiLang,
): Promise<CLTVKPIResponse> => {
  const query = buildKpiParams(primary_date, secondary_date, lang);
  const url = `${API_BASE_URL}/cltv/dashboard/kpis?${query}`;
  const response = await fetch(url, {
    method: "GET",
    headers: buildAuthHeaders("api_key"),
  });
  return handleJsonResponse<CLTVKPIResponse>(response);
};

/**
 * Stamps every localized distribution row with its canonical English name. Both bundles describe
 * the same period, so the counts are identical: rows are matched on `count`, and on position when
 * a count is shared by two segments.
 */
const withCanonicalSegmentNames = (
  bundle: CLTVKPIResponse,
  englishBundle: CLTVKPIResponse,
): CLTVKPIResponse => {
  const localizedSegments = bundle.segmentDistribution ?? [];
  const englishSegments = englishBundle.segmentDistribution ?? [];

  if (!localizedSegments.length || englishSegments.length !== localizedSegments.length) {
    return bundle;
  }

  /** `null` marks a count two segments share, so it can never key a name. */
  const nameByCount = new Map<number, string | null>();
  for (const segment of englishSegments) {
    nameByCount.set(segment.count, nameByCount.has(segment.count) ? null : segment.name);
  }

  return {
    ...bundle,
    segmentDistribution: localizedSegments.map((segment, index) => ({
      ...segment,
      nameKey: nameByCount.get(segment.count) ?? englishSegments[index]?.name,
    })),
  };
};

export async function fetchCLTVKpis(
  primary_date: string,
  secondary_date: string,
  lang: ApiLang,
): Promise<CLTVKPIResponse> {
  const apiLang = normalizeApiLang(lang);

  if (apiLang === "en") {
    return fetchKpiBundle(primary_date, secondary_date, apiLang);
  }

  // The localized bundle carries the copy and the localized digits the dashboard renders; the
  // English one is read for segment names only, and a failure there just leaves `nameKey` unset.
  const [bundle, englishBundle] = await Promise.all([
    fetchKpiBundle(primary_date, secondary_date, apiLang),
    fetchKpiBundle(primary_date, secondary_date, "en").catch(() => null),
  ]);

  return englishBundle ? withCanonicalSegmentNames(bundle, englishBundle) : bundle;
}

export async function fetchCLTVSpendingPatterns(
  lang: ApiLang,
): Promise<SpendingPatternsResponse> {
  const query = new URLSearchParams({
    lang: normalizeApiLang(lang),
    api_key: getAuthToken("api_key"),
  }).toString();
  const url = `${API_BASE_URL}/cltv/dashboard/spending-patterns?${query}`;
  const response = await fetch(url, {
    method: "GET",
    headers: buildAuthHeaders("api_key"),
  });
  return handleJsonResponse<SpendingPatternsResponse>(response);
}

export async function fetchCLTVSegmentEvolution(
  primary_date: string,
  lang: ApiLang,
): Promise<SegmentEvolutionResponse> {
  const query = buildPrimaryDateParams(primary_date, lang);
  const url = `${API_BASE_URL}/cltv/dashboard/segment-evolution?${query}`;
  const response = await fetch(url, {
    method: "GET",
    headers: buildAuthHeaders("api_key"),
  });
  return handleJsonResponse<SegmentEvolutionResponse>(response);
}

export async function fetchCLTVCustomerAnalytics(
  params: FetchCustomerAnalyticsParams
): Promise<CustomerAnalyticsResponse> {
  const searchParams = new URLSearchParams({
    primary_date: params.primary_date,
    lang: normalizeApiLang(params.lang),
    api_key: getAuthToken("api_key"),
  });
  if (params.page !== undefined) {
    searchParams.set("page", String(params.page));
  }
  if (params.page_size !== undefined) {
    searchParams.set("page_size", String(params.page_size));
  }
  if (params.search) {
    searchParams.set("search", params.search);
  }
  if (params.segment?.length) {
    for (const seg of params.segment) {
      searchParams.append("segment", seg);
    }
  }
  if (params.sort_by) {
    searchParams.set("sort_by", params.sort_by);
  }
  if (params.sort_order) {
    searchParams.set("sort_order", params.sort_order);
  }

  const url = `${API_BASE_URL}/cltv/dashboard/customer-analytics?${searchParams.toString()}`;
  const response = await fetch(url, {
    method: "GET",
    headers: buildAuthHeaders("api_key"),
  });
  const json = await handleJsonResponse<unknown>(response);
  return normalizeCustomerAnalyticsResponse(json);
}

export async function fetchCLTVCustomerAnalyticsSegments(
  primary_date: string,
  lang: ApiLang,
): Promise<CustomerAnalyticsSegmentsResponse> {
  const query = buildPrimaryDateParams(primary_date, lang);
  const url = `${API_BASE_URL}/cltv/dashboard/customer-analytics/segments?${query}`;
  const response = await fetch(url, {
    method: "GET",
    headers: buildAuthHeaders("api_key"),
  });
  const json = await handleJsonResponse<CustomerAnalyticsSegmentsResponse>(response);
  return {
    segments: Array.isArray(json.segments) ? json.segments : [],
  };
}
